import { useRouter } from "expo-router";
import React, { useCallback } from "react";
import { Alert, Platform } from "react-native";

import OrganizationItemView from "./OrganizationItemView";

import { AnalyticsTracker } from "@/features/analytics";
import { useCurrentUser } from "@/features/auth";
import {
  AuthMeDocument,
  InvitationsOrganizationFragment,
  useInvitationsLogIntoOrganizationMutation,
} from "@/generated/graphql";
import useRecordUserGoal, { GoalType } from "@/hooks/useRecordUserGoal";
import { useTheme } from "@/providers/ThemeProvider";
import {
  resetOrgScopedState,
  resetPerUserState,
} from "@/stores/resetPerUserState";

/**
 * Props for the OrganizationItemContainer component.
 */
interface Props {
  item: InvitationsOrganizationFragment;
}

/**
 * Container component for managing a single organization item with login and edit capabilities.
 * @param root0 - The component props
 * @param root0.item - The organization data fragment containing organization details, sport, and currency information
 */
const OrganizationItemContainer = ({ item }: Props) => {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";

  const recordLogIntoOrganization = useRecordUserGoal({
    goalType: GoalType.LogIntoOrganization,
    value: item.id,
  });

  const currentUser = useCurrentUser();
  const isAdmin = currentUser?.isAdmin;

  const handleResetVar = useCallback(async () => {
    /*
     * SE-7721: this file, `ReceivedInvitationItemContainer`, and sign-out
     * (`useSettingsActions`) each used to hand-maintain their own copy of the
     * per-user reset list, which is exactly how they drifted: SE-7701 had to
     * add `filterValuesVar` to two of them separately, and SE-7720's outbox
     * clear was added here and to sign-out as two more edits. Both this file
     * and `ReceivedInvitationItemContainer` also run the SAME kind of
     * transition — a switch of the acting organisation via
     * `logIntoOrganization` — so the org-scoped clear below is shared too.
     * Two callables, three call sites, no per-site divergence left to drift.
     *
     * On the evaluation outbox specifically (SE-8075): neither call clears
     * it, and the AsyncStorage wipe keeps its key. Each parked write records
     * the user AND organisation that queued it, and the drain replays only the
     * current session's own — so the entries parked in the organisation being
     * left wait for this user to act in it again, instead of draining into the
     * newly selected one (where the server refuses them and the drain would
     * discard them) or being destroyed.
     */
    resetPerUserState();
    await resetOrgScopedState();
  }, []);

  const [login, { loading: loginLoading, client }] =
    useInvitationsLogIntoOrganizationMutation({
      onCompleted: async response => {
        try {
          recordLogIntoOrganization();
          await handleResetVar();
          AnalyticsTracker.user.logOrganizationJoined({
            user_id: currentUser?.id || "",
            organization_id: item.id,
            organization_name: item.name,
            phone_number: response?.logIntoOrganization?.phoneNumber ?? "",
            is_admin: response?.logIntoOrganization?.isAdmin ?? false,
            user_role: response?.logIntoOrganization?.title || "",
            currency_code: item.currencyCode,
            sport_id: item.sport?.id || "",
            sport_name: item.sport?.name || "",
            timestamp: new Date().toISOString(),
          });
          // Associate user with organization in PostHog for group analytics
          AnalyticsTracker.setGroup("organization", item.id, {
            name: item.name,
          });

          if (Platform.OS === "web") {
            // SE-4983: the previous History-API + synthetic popstate
            // workaround (originally from SE-3726, which targeted the
            // path INTO /organizations) raced with the Apollo store
            // reset + AsyncStorage wipe and left the left sidebar
            // unmounted on web until the user manually reloaded.
            // `router.replace("/")` doesn't work either: it dispatches
            // a REPLACE action with `name: index` into the navigator
            // that owns /organizations, which has no `index` child —
            // expo-router rejects the action with "was not handled by
            // any navigator". A hard navigation to / is the only
            // option that reliably yields a clean root render with the
            // new org's identity. Same UX the user used to recover
            // manually (Cmd-R), but automatic.
            window.location.assign("/");
            return;
          }

          // Native (iOS/Android): the navigator stack and Apollo cache
          // behave correctly under a normal router.replace, so keep
          // the SPA transition there.
          if (response?.logIntoOrganization) {
            client.writeQuery({
              query: AuthMeDocument,
              data: { me: response.logIntoOrganization },
            });
          }
          await client.resetStore();
          router.replace("/");
        } catch (error) {
          console.error("Failed to complete organization login:", error);
          // Deliberately fail CLOSED, not open: resetOrgScopedState throwing
          // means the previous organisation's per-user/org-scoped state
          // (filters, watchlist selections, recently-viewed players, the
          // AsyncStorage wipe) did NOT complete, so proceeding to
          // client.resetStore()/router.replace() here would carry that
          // residue into the newly selected organisation — the exact
          // mixed-org leak class this reset exists to prevent (see the
          // "fail closed" test in this component's test file). The one gap
          // that failing closed left is that the user was given no signal at
          // all beyond a console.error nobody sees on device, so surface one.
          const message =
            "Something went wrong switching organizations. Please try again.";
          if (Platform.OS === "web") {
            window.alert(message);
          } else {
            Alert.alert("Organization switch failed", message);
          }
        }
      },
      variables: { input: { organizationId: item.id } },
    });

  const router = useRouter();

  const recordLeftOrganizationTracking = useCallback(() => {
    AnalyticsTracker.user.logOrganizationLeft({
      user_id: currentUser?.id || "",
      organization_id: currentUser?.organizationId || "",
      organization_name: currentUser?.organization?.name || "",
      phone_number: currentUser?.phoneNumber || "",
      is_admin: currentUser?.isAdmin || false,
      user_role: currentUser?.title || null,
      left_at: new Date().toISOString(),
    });
  }, [currentUser]);

  const handleLogin = useCallback(() => {
    if (Platform.OS === "web") {
      const doLogin = window.confirm(
        `Are you sure you want to log into this organization?`
      );

      if (doLogin) {
        recordLeftOrganizationTracking();
      }

      return doLogin ? login() : null;
    } else {
      return new Promise((resolve, reject) =>
        Alert.alert(
          `Log into organization`,
          `Are you sure you want to log into this organization?`,
          [
            {
              text: "Cancel",
              style: "cancel",
            },
            {
              text: "Yes",
              onPress: () => {
                recordLeftOrganizationTracking();
                login().then(resolve).catch(reject);
              },
              style: "destructive",
            },
          ]
        )
      );
    }
  }, [login, recordLeftOrganizationTracking]);

  return (
    <OrganizationItemView
      item={item}
      loading={loginLoading}
      onLogInto={handleLogin}
      isAdmin={!!isAdmin}
      isDark={isDark}
    />
  );
};

export default OrganizationItemContainer;
